import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { authFetch, useAuth } from "../context/AuthContext";
import { fileUrl } from "../components/PhotoGallery";
import ShareProfileButton from "../components/ShareProfileButton";
import { ChatCircle, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

export default function UserProfile() {
    const { userId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [target, setTarget] = useState(null);
    const [compat, setCompat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        authFetch("get", `/matches/compatibility/${userId}`)
            .then((r) => { setTarget(r.data.target); setCompat(r.data.compatibility); })
            .catch(() => toast.error("Could not load profile"))
            .finally(() => setLoading(false));
    }, [userId]);

    const messageThem = async () => {
        if (starting) return;
        setStarting(true);
        try {
            const res = await authFetch("post", "/messages/start-dm", { target_user_id: userId });
            navigate(`/messages/${res.data.match_id}`);
        } catch (err) {
            const detail = err.response?.data?.detail || "Could not start conversation";
            if (err.response?.status === 403) {
                toast.error("Premium needed to message non-matches");
            } else {
                toast.error(detail);
            }
        } finally {
            setStarting(false);
        }
    };

    if (loading) {
        return (<><Navbar /><main className="min-h-screen flex items-center justify-center"><p className="text-muted">Reading their stars...</p></main></>);
    }
    if (!target) return null;

    const canMessage = user?.subscription_active || target.subscription_active;

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-4 sm:px-6">
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* HEADER CARD */}
                    <div className="glass-gold p-6 sm:p-8">
                        <div className="flex flex-col sm:flex-row gap-6">
                            <div className="w-32 h-32 rounded-2xl bg-white/5 border border-gold/30 overflow-hidden flex items-center justify-center text-5xl text-gold flex-shrink-0 mx-auto sm:mx-0">
                                {target.photos?.[0] ? (
                                    <img src={fileUrl(target.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-serif">{SIGN_GLYPH[target.sun]}</span>
                                )}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <div className="flex items-center gap-2 justify-center sm:justify-start">
                                    <h1 className="font-serif text-4xl" data-testid="profile-name">{target.name}{target.age ? `, ${target.age}` : ""}</h1>
                                    {target.verified && (
                                        <ShieldCheck size={22} weight="fill" className="text-gold" title="Verified" />
                                    )}
                                </div>
                                <p className="text-muted mt-1 text-sm">{target.birth_location}</p>
                                <p className="text-muted mt-2 font-serif">
                                    {SIGN_GLYPH[target.sun]} {target.sun} · ☽ {target.moon} · ↑ {target.rising}
                                </p>
                                {target.bio && <p className="font-serif mt-4 italic text-muted">"{target.bio}"</p>}
                            </div>
                            <div className="text-center">
                                <p className="font-serif text-5xl text-gold leading-none" data-testid="profile-score">{compat?.overall}</p>
                                <p className="label-tracking text-xs">Compatibility</p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-6 justify-center sm:justify-start">
                            <button
                                onClick={messageThem}
                                disabled={starting || !canMessage}
                                className="btn-gold-solid inline-flex items-center gap-2"
                                data-testid="profile-message-btn"
                            >
                                <ChatCircle size={16} weight="fill" />
                                {starting ? "Opening..." : (canMessage ? "Message" : "Premium to Message")}
                            </button>
                            <Link to={`/compatibility/${target.user_id}`} className="btn-gold inline-flex items-center gap-2" data-testid="profile-synastry-btn">
                                <Sparkle size={16} weight="duotone" /> Synastry
                            </Link>
                            <ShareProfileButton
                                userId={target.user_id}
                                name={target.name}
                                sun={target.sun}
                                moon={target.moon}
                                rising={target.rising}
                            />
                        </div>
                        {!canMessage && (
                            <p className="text-xs text-muted mt-3 text-center sm:text-left">
                                Upgrade to Premium to message anyone — or wait until they mutually like you.
                            </p>
                        )}
                    </div>

                    {/* PHOTOS */}
                    {target.photos && target.photos.length > 1 && (
                        <div className="glass p-6">
                            <p className="label-tracking text-gold">Gallery</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                                {target.photos.slice(1).map((p) => (
                                    <div key={p.photo_id} className="aspect-square rounded-xl overflow-hidden border border-white/10">
                                        <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}
